import {Injectable} from "@angular/core";
import {LogCat, LogManager} from "../../../common/log";
import {APIProxy, HttpObservable} from "../../../common/http";
import {
	IAttachmentOptions,
	IChatCredentials, ICustomWorklistFavorites,
	IDefaultResourceRoleAndLocation, IDocumentTogetherOptions,
	IFavoritesDefaultTabOptions,
	ILatestLocation, IPatientLetterOptions, ILinkingOptions,
	IMessagesOptions, IModalityAvailabilityViewerOptions,
	INavigationBarOptions,
	INotificationOptions,
	IPatientChartOptions, IPatientLogOptions,
	IPatientSearchOptions,
	IPatientSummaryOptions,
	ISchedulerOptions, IUserGroupDefaultWL,
	IWorkListOptions
} from "../interfaces/Iuser.preferences";
import {ILocation} from "../../org.struct/interfaces/favorite.locations";
import {IFavoriteStudy} from "../../procedures/interfaces/Ifavorite.study";
import {IAnotherUserDetails, IUserContextWithAuth} from "..";
import {IFavoriteWorklist, UserGroupFavoriteWorklists} from "../../worklists/interfaces/IFavoriteWorklist";
import {ICategoryWorklistsOrder} from "../../worklists/interfaces/Iworklist.order";
import {InavbarCustomWorklist} from "../../worklists/interfaces/Inavbar.custom.worklist";
import {
	PopupsPositions
} from "../../../common/ui/components/popup/popup.component/popup.position.store";
import {convertMapToObj} from "../../../common/http/services/http.wrapper";

/**
	****************************************************************
	* Proxy service class for user context data retrieval.
	*
	* Written by: Shai Zelman
	****************************************************************
	*/
@Injectable({ providedIn: 'root' })
  export class UserContextProxy {
	constructor(private clinicalProxy: APIProxy) {
	}


	public getAnotherUserDetails(personKey:number): HttpObservable<IAnotherUserDetails> {
		LogManager.debug(LogCat.RisUserContextCategories.Proxy, "Get another user details.");
		return this.clinicalProxy.get("UserContext", "GetAnotherUserContext",{pName:'personKey',pVal:personKey});
	}

	/**
	 * Gets the user context for the current logged in user in IUserContext format from the server.
	 * @param clientUtcOffsetMinutes should be provided only during the login
	 * @returns A promise containing an IUserContext object.
	 */
	public getCurrentUserContext(clientUtcOffsetMinutes:number): HttpObservable<IUserContextWithAuth> {
		LogManager.debug(LogCat.RisUserContextCategories.Proxy, "Get current user context.");
		return this.clinicalProxy.get("UserContext", "GetCurrentUserContext",{pName:'clientUtcOffsetMinutes',pVal:clientUtcOffsetMinutes});
	}

	public setFavoritesStudies(favoritesStudies: IFavoriteStudy[]): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetFavoriteStudies", favoritesStudies);
	}

	public setFavoritesDefaultTab(favoritesDefaultTab: IFavoritesDefaultTabOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetFavoritesDefaultTab", favoritesDefaultTab);
	}

	public setMessagesOptions(messagesOptions: IMessagesOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetMessagesOptions", messagesOptions);
	}

	public setChatCredentials(credentials: IChatCredentials): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetChatCredentials", credentials);
	}

	public setFavoriteLocations(favoriteLocations: ILocation[]): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetFavoriteLocations", favoriteLocations);
	}

	public setAttachmentOptions(attachmentOptions: IAttachmentOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetAttachmentOptions", attachmentOptions);
	}

	public setWorklistOptions(worklistOptions: IWorkListOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetWorklistOptions", worklistOptions);
	}

	public setLinkingOptions(linkingOptions: ILinkingOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetLinkingOptions", linkingOptions);
	}

	public setPopupLocations(popupOptions: PopupsPositions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetPopupLocations", convertMapToObj(popupOptions));
	}


	public setNavigationBarOptions(navigationBarOptions: INavigationBarOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetNavigationBarOptions", navigationBarOptions);
	}

	public setPatientSummaryOptions(patientSummaryOptions: IPatientSummaryOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetPatientSummaryOptions", patientSummaryOptions);
	}

	public setSchedulerOptions(schedulerOptions: ISchedulerOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetSchedulerOptions", schedulerOptions);
	}

	public setPatientChartOptions(patientChartOptions: IPatientChartOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetPatientChartOptions", patientChartOptions);
	}

	public setDefaultUserLanguage(languageKey: number): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetDefaultUserLanguage", languageKey);
	}

	public setLastSelectedUserGroup(roleGroupKey: number): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetLastSelectedUserGroup", roleGroupKey);
	}

	public getUserHiddenGroups(): HttpObservable<number[]>{
		return this.clinicalProxy.get("UserContext", "GetUserHiddenGroups");
	}

	public setDefaultResourceRole(defaultResourceRoleAndLocation: IDefaultResourceRoleAndLocation): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetDefaultResourceRole", defaultResourceRoleAndLocation);
	}

	public setLatestLocation(latestLocation: ILatestLocation): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetLatestLocation", latestLocation);
	}

	public setPatientSearchOptions(patientSearchOptions: IPatientSearchOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetPatientSearchOptions", patientSearchOptions);
	}

	public setDocumentTogetherOptions(documentTogetherOptions: IDocumentTogetherOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetDocumentTogetherOptions", documentTogetherOptions);
	}

	public setDefaultPatientClass(patientClassKey: number): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetDefaultPatientClass", patientClassKey);
	}

	public setDefaultModalityScheduleGroup(scheduleGroupKey: number): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "setDefaultModalityScheduleGroup", scheduleGroupKey);
	}

	public setNotificationOptions(notificationOptions: INotificationOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetNotificationOptions", notificationOptions);
	}

	public setPatientLogOptions(patientLogOptions: IPatientLogOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetPatientLogOptions", patientLogOptions);
	}

	public setFavoriteWorklists_old(favoriteWorklists: IFavoriteWorklist[]): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetFavoriteWorklists", favoriteWorklists);
	}
	public setFavoriteWorklists(userGroupFavoriteWorklists: UserGroupFavoriteWorklists): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetFavoriteWorklists", userGroupFavoriteWorklists);
	}

	public setCategoryWorklistOrder(categoryWorklistsOrder: ICategoryWorklistsOrder): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetCategoryWorklistOrder", categoryWorklistsOrder);
	}

	public setDefaultWorklist(userGroupDefaultWl:IUserGroupDefaultWL):HttpObservable<void>{
		return this.clinicalProxy.post("UserContext", "SetUserGroupDefaultWorklist", userGroupDefaultWl);
	}


	//Only for testing purposes
	public deleteWorklistOrder(): HttpObservable<void> {
		return this.clinicalProxy.delete("UserContext", "DeleteWorklistOrderUserPreference", {pName: "stam", pVal: "stam"});
	}
	//Only for testing purposes
	public deleteDynamicWorklist(): HttpObservable<void> {
		return this.clinicalProxy.delete("UserContext", "DeleteDynamicWorklistUserPreference", {pName: "stam", pVal: "stam"});
	}

	public setCustomWorklistFavorites(customWLFavorites: ICustomWorklistFavorites): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetCustomWorklistFavorites", customWLFavorites);
	}

	public addNavBarCustomWorklist(worklist: InavbarCustomWorklist): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "AddNavBarCustomWorklist", worklist);
	}

	public removeNavBarCustomWorklist(worklist: InavbarCustomWorklist): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "RemoveNavBarCustomWorklist", worklist);
	}

	public setModalityAvailabilityViewerOptions(mavOptions: IModalityAvailabilityViewerOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "SetModalityAvailabilityViewerOptions", mavOptions);
	}

	public setPatientLetterOptions(letterOptions: IPatientLetterOptions): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "setPatientLetterOptions", letterOptions);
	}
	/*public addNavBarCustomWorklists(worklists:InavbarCustomWorklist[]): HttpObservable<void> {
		return this.clinicalProxy.post("UserContext", "AddNavBarCustomWorklists", worklists);
	}*/

	// public removeNavBarCustomWorklists(worklists:InavbarCustomWorklist[]): HttpObservable<void> {
	// 	return this.clinicalProxy.post("UserContext", "RemoveNavBarCustomWorklists", worklists);
	// }

}
